import { useUserStore, userStoreSelector } from '@editor/stores/userStore';

import { useWorkspacesFeatureEnabled } from 'global/growthbookUtils';

import { useActiveWorkspaceId } from './useActiveWorkspaceId';

const buildUrl = (prefix: string, suffix: string, ignorePrefix: boolean) => {
  if (ignorePrefix) {
    return suffix;
  }
  if (suffix === '/') {
    return prefix;
  }
  return `${prefix}${suffix}`;
};

export const useUrlWithWorkspacePrefix = (opts: {
  href?: string;
  ignorePrefix?: boolean;
}): string | undefined => {
  // if workspaces is not enabled, we prefix everything with `/user/dashboard` instead so we are backwards compatible
  const workspaceEnabled = useWorkspacesFeatureEnabled();
  const workspaceFromStore = useActiveWorkspaceId();

  const userWorkspaceId = useUserStore(
    userStoreSelector.user
  )?.personalWorkspaceId;

  const workspaceToLoad = workspaceFromStore || userWorkspaceId;

  if (workspaceEnabled && !workspaceToLoad) {
    return undefined;
  }

  // TODO: simplify this function once we remove the FF for workspaces - remove this `if` block
  if (workspaceEnabled === false) {
    // if we are in legacy mode, what is now called `workspace/:id/projects` used to be `user/dashboard` so we need to handle
    // that special case
    let suffix = opts.href ?? '';
    if (suffix.startsWith('/projects')) {
      suffix = suffix.substring('/projects'.length);
    }
    // we also have `/workspace/:id/projects/folder/:fid` which used to be `/user/dashboard/:fid`
    if (suffix.startsWith('/folder')) {
      suffix = suffix.substring('/folder'.length);
    }

    // templates is also a custom path in legacy mode - its just /templates so we need to handle that too
    if (suffix.startsWith('/templates')) {
      return '/templates';
    }

    if (suffix === '/') {
      return '/';
    }
    return buildUrl('/user/dashboard', suffix, opts.ignorePrefix ?? false);
  }

  return buildUrl(
    `/workspace/${workspaceToLoad || userWorkspaceId}`,
    opts.href ?? '',
    opts.ignorePrefix ?? false
  );
};
